import axios from "axios";
import { baseUrl } from ".";

// OAuth Types
export type OAuthProvider = 'google' | 'facebook';

/**
 * Get the OAuth URL for a specific provider
 * @param provider - The OAuth provider ('google' or 'facebook')
 * @param appId - The whitelabel appId for multi-tenancy
 * @param frontendUrl - Optional frontend URL for redirect after auth
 * @returns The full OAuth initiation URL
 */
export const getOAuthUrl = (
  provider: OAuthProvider,
  appId: string,
  frontendUrl?: string
): string => {
  const params = new URLSearchParams({
    appId,
    ...(frontendUrl && { frontendUrl }),
  });
  return `${baseUrl}/whitelabel/auth/${provider}?${params.toString()}`;
};

/**
 * Initiate OAuth flow by redirecting to the provider
 * @param provider - The OAuth provider ('google' or 'facebook')
 * @param appId - The whitelabel appId for multi-tenancy
 */
export const initiateOAuth = (provider: OAuthProvider, appId: string): void => {
  const frontendUrl = window.location.origin;
  const oauthUrl = getOAuthUrl(provider, appId, frontendUrl);
  window.location.href = oauthUrl;
};

export interface RegisterProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  termsAccepted?: boolean; // Optional - T&C acceptance now handled via modal before registration
}

export interface VerifyProps {
  email: string;
  otp: Number;
}

export interface VerifyForgetProps {
  userHandle: string;
  otp: string;
  password: string;
}

export interface UpdateProfileProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface LoginProps {
  email: string;
  password: string;
}

export interface ResultMessageProps {
  message: string;
  status: number;
}

export interface ResultLoginProps {
  message: string;
  token: string;
  user: {
    id: string;
    fullname: string;
    email: string;
    userHandle: string;
    accountStatus?: 'active' | 'pending_deletion' | 'deleted';
    deletionScheduledFor?: string | null;
  };
  status: number;
}

/**
 * Complete OAuth registration after T&C acceptance
 * Called from OAuthTermsRequired page after user accepts terms
 * @param pendingToken - The temporary token from the OAuth callback
 * @returns Login response with JWT token
 */
export const completeOAuthRegistration = async (
  pendingToken: string
): Promise<ResultLoginProps> => {
  try {
    const response = await axios.post(
      `${baseUrl}/whitelabel/auth/complete-oauth`,
      { pendingToken },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
        },
      }
    );

    return {
      message: response.data.message || "Account created successfully",
      status: response.status,
      token: response.data.token,
      user: response.data.user,
    };
  } catch (error: any) {
    return {
      message: error.response?.data?.message || "Failed to complete registration",
      status: error.response?.status || 500,
      token: "",
      user: {
        id: "",
        fullname: "",
        email: "",
        userHandle: "",
      },
    };
  }
};

// User Deletion Interfaces
export interface UserDeletionSettings {
  deletionRequested: boolean;
  deletionRequestedAt?: string | null;
  deletionScheduledFor?: string | null;
  deletionCancellationToken?: string | null;
  accountStatus: 'active' | 'pending_deletion' | 'deleted';
}

export interface UserProfileWithDeletion {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  userHandle: string;
  connectedTo: string;
  verified: boolean;
  status: boolean;
  createdAt?: string;
  updatedAt?: string;
  notificationSettings?: {
    email: boolean;
    push: boolean;
    sms: boolean;
    marketing: boolean;
  };
  privacySettings?: {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    functional: boolean;
  };
  // New deletion fields
  deletionRequested: boolean;
  deletionRequestedAt?: string | null;
  deletionScheduledFor?: string | null;
  deletionCancellationToken?: string | null;
  accountStatus: 'active' | 'pending_deletion' | 'deleted';
}

export interface RequestAccountDeletionProps {
  confirmEmail: string;
  reason?: string;
}

export interface DeletionRequestResponse {
  message: string;
  status: number;
  deletionScheduledFor?: string;
  cancellationToken?: string;
}

export interface ChangePasswordProps {
  password: string;
  passwordVerify: string;
}

export interface ChangePasswordResponse {
  message: string;
  status: number;
}

export const registerUser = async (
  data: RegisterProps
): Promise<ResultMessageProps> => {
  try {
    const response = await axios.post(
      baseUrl + "/whitelabel/registerClientNew",
      data,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );

    return { status: 201, message: response.data.message };
  } catch (error: any) {
    return {
      status: error.response.status,
      message: error.response.data.message,
    };
  }
};

export const verifyOTP = async (
  data: VerifyProps
): Promise<ResultMessageProps> => {
  try {
    const response = await axios.post(baseUrl + "/whitelabel/verifyOTP", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${window.__TENANT__.token}`,
      },
    });
    return { status: 200, message: response.data.message };
  } catch (error: any) {
    return { status: 400, message: error.response.data.message };
  }
};

export const loginUser = async (
  data: LoginProps
): Promise<ResultLoginProps> => {
  try {
    const response = await axios.post(baseUrl + "/whitelabel/loginNew", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${window.__TENANT__.token}`,
      },
    });
    return { status: 200, ...response.data };
  } catch (error: any) {
    return {
      status: error.response.status,
      message: error.response.data.message,
      token: "",
      user: {
        id: "",
        fullname: "",
        email: "",
        userHandle: "",
      },
    };
  }
};

export const forgetPassword = async (
  email: string
): Promise<ResultLoginProps> => {
  try {
    const response = await axios.post(
      baseUrl + "/whitelabel/forgetPasswordNew",
      { email: email },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return { status: 200, ...response.data };
  } catch (error: any) {
    console.log(error);
    return {
      status: error.response?.status ?? 400,
      message: error.response?.data?.message ?? "Something went wrong",
      token: "",
      user: {
        id: "",
        fullname: "",
        email: "",
        userHandle: "",
      },
    };
  }
};

export const forgetPasswordOTP = async (
  data: VerifyForgetProps
): Promise<ResultLoginProps> => {
  try {
    const response = await axios.put(
      baseUrl + "/whitelabel/setNewPasswordNew",
      data,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${window.__TENANT__.token}`,
        },
      }
    );
    return { status: 200, ...response.data };
  } catch (error: any) {
    console.log(error);
    return {
      status: 400,
      message: error.response.data.message,
      token: "",
      user: {
        id: "",
        fullname: "",
        email: "",
        userHandle: "",
      },
    };
  }
};

// New function to handle both registration and login
export const registerAndLoginUser = async (
  data: RegisterProps
): Promise<ResultLoginProps> => {
  try {
    // First register the user
    const registerResponse = await registerUser(data);
    if (registerResponse.status === 201) {
      // If registration is successful, log in the user
      const loginResponse = await loginUser({
        email: data.email,
        password: data.password,
      });
      return loginResponse;
    } else {
      return {
        status: registerResponse.status,
        message: registerResponse.message,
        token: "",
        user: {
          id: "",
          fullname: "",
          email: "",
          userHandle: "",
        },
      };
    }
  } catch (error: any) {
    return {
      status: error.response.status,
      message: error.response.data.message,
      token: "",
      user: {
        id: "",
        fullname: "",
        email: "",
        userHandle: "",
      },
    };
  }
};

// Add this new function to fetch user profile data
export const fetchUserProfile = async (token: string): Promise<UserProfileWithDeletion | null> => {
  try {
    console.log('🔑 Token received:', !!token);
    
    if (!token) {
      console.log('❌ No JWT token provided');
      return null;
    }

    // Use the same token handling as the working favorites functionality
    const jwt = (token as string).replace("B9I9N9456", "");
    
    console.log('🔑 JWT token length:', jwt.length);
    console.log('🔑 JWT token preview:', jwt.substring(0, 50) + '...');

    // Use the correct endpoint that matches your backend
    console.log('🌐 Making API call to:', baseUrl + "/whitelabel/userProfile");
    
    const response = await axios.get(baseUrl + "/whitelabel/userProfile", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    console.log('📡 API Response status:', response.status);
    console.log('📡 API Response data:', response.data);

    if (response.status === 200 && response.data && response.data.results) {
      console.log('✅ User profile fetched successfully:', response.data.results);
      // Transform the data to match our expected format
      return {
        firstName: response.data.results.firstName || '',
        lastName: response.data.results.lastName || '',
        email: response.data.results.email || '',
        phone: response.data.results.phone || '',
        userHandle: response.data.results.userHandle || '',
        connectedTo: response.data.results.connectedTo || '',
        verified: response.data.results.verified || false,
        status: response.data.results.status || true,
        createdAt: response.data.results.createdAt || '',
        updatedAt: response.data.results.updatedAt || '',
        notificationSettings: response.data.results.notificationSettings || {
          email: true,
          push: false,
          sms: false,
          marketing: true
        },
        privacySettings: response.data.results.privacySettings || {
          essential: true,
          analytics: true,
          marketing: true,
          functional: true
        },
        // New deletion fields
        deletionRequested: response.data.results.deletionRequested || false,
        deletionRequestedAt: response.data.results.deletionRequestedAt || null,
        deletionScheduledFor: response.data.results.deletionScheduledFor || null,
        deletionCancellationToken: response.data.results.deletionCancellationToken || null,
        accountStatus: response.data.results.accountStatus || 'active'
      };
    } else {
      console.log('❌ Failed to fetch user profile:', response.data);
      return null;
    }
  } catch (error: any) {
    console.error('❌ Error fetching user profile:', error);
    if (error.response) {
      console.error('❌ Error response:', error.response.data);
      console.error('❌ Error status:', error.response.status);
    }
    return null;
  }
};

// Add this new function to update user profile data
export const updateUserProfile = async (token: string, data: UpdateProfileProps): Promise<{
  message: string;
  status: number;
  results?: UserProfileWithDeletion;
}> => {
  try {
    console.log('📝 Starting updateUserProfile...');
    console.log('🔑 Token received:', !!token);
    console.log('📝 Data to update:', data);
    
    if (!token) {
      console.log('❌ No JWT token provided');
      return { status: 401, message: 'No authentication token provided' };
    }

    // Use the same token handling as the working favorites functionality
    const jwt = (token as string).replace("B9I9N9456", "");
    
    console.log('🔑 JWT token length:', jwt.length);
    console.log('🔑 JWT token preview:', jwt.substring(0, 50) + '...');

    // Use the correct endpoint that matches your backend
    console.log('🌐 Making API call to:', baseUrl + "/whitelabel/userProfile");
    
    const response = await axios.put(baseUrl + "/whitelabel/userProfile", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    console.log('📡 API Response status:', response.status);
    console.log('📡 API Response data:', response.data);

    if (response.status === 200 && response.data) {
      console.log('✅ User profile updated successfully:', response.data.results);
      return {
        status: 200,
        message: response.data.message,
        results: response.data.results
      };
    } else {
      console.log('❌ Failed to update user profile:', response.data);
      return {
        status: response.status || 400,
        message: response.data?.message || 'Failed to update profile'
      };
    }
  } catch (error: any) {
    console.error('❌ Error updating user profile:', error);
    if (error.response) {
      console.error('❌ Error response:', error.response.data);
      console.error('❌ Error status:', error.response.status);
      return {
        status: error.response.status,
        message: error.response.data?.message || 'Failed to update profile'
      };
    }
    return {
      status: 500,
      message: 'Network error occurred while updating profile'
    };
  }
};

// Add this interface for service applications
export interface ServiceApplication {
  id: string;
  serviceName: string;
  appliedDate: string;
  status: 'pending' | 'approved' | 'rejected';
  productId: string;
  name: string;
  email: string;
  phoneNumber: string;
  formCompleted?: boolean;
  calendarCompleted?: boolean;
}

// Add this function to fetch user's service applications
export const fetchUserEnquiries = async (token: string): Promise<ServiceApplication[]> => {
  try {
    console.log('📝 Starting fetchUserEnquiries...');
    console.log('🔑 Token received:', !!token);
    
    if (!token) {
      console.log('❌ No JWT token provided');
      return [];
    }

    // Use the same token handling as other functions
    const jwt = (token as string).replace("B9I9N9456", "");
    
    console.log('🔑 JWT token length:', jwt.length);

    // Make API call to fetch user enquiries
    console.log('🌐 Making API call to:', baseUrl + "/whitelabel/userEnquiries");
    
    const response = await axios.get(baseUrl + "/whitelabel/userEnquiries", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    console.log('📡 API Response status:', response.status);
    console.log('📡 API Response data:', response.data);

    if (response.status === 200 && response.data && response.data.results) {
      return response.data.results;
    } else {
      console.log('❌ Invalid response format');
      return [];
    }
  } catch (error) {
    console.error('❌ Error fetching user enquiries:', error);
    return [];
  }
};

export const fetchUserSettings = async (token: string): Promise<{
  notificationSettings: {
    email: boolean;
    push: boolean;
    sms: boolean;
    marketing: boolean;
    /** Absent on older accounts — treat as enabled */
    savedSearchAlerts?: boolean;
  };
  privacySettings: {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    functional: boolean;
  };
  deletionSettings: UserDeletionSettings;
} | null> => {
  try {
    if (!token) return null;
    
    const jwt = (token as string).replace("B9I9N9456", "");
    
    const response = await axios.get(baseUrl + "/whitelabel/userSettings", {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    if (response.status === 200 && response.data && response.data.results) {
      return {
        ...response.data.results,
        deletionSettings: {
          deletionRequested: response.data.results.deletionRequested || false,
          deletionRequestedAt: response.data.results.deletionRequestedAt || null,
          deletionScheduledFor: response.data.results.deletionScheduledFor || null,
          deletionCancellationToken: response.data.results.deletionCancellationToken || null,
          accountStatus: response.data.results.accountStatus || 'active'
        }
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user settings:', error);
    return null;
  }
};

export const updateNotificationSettings = async (token: string, settings: {
  email: boolean;
  push: boolean;
  sms: boolean;
  marketing: boolean;
  savedSearchAlerts?: boolean;
}): Promise<{ message: string; status: number }> => {
  try {
    if (!token) return { status: 401, message: 'No authentication token provided' };
    
    const jwt = (token as string).replace("B9I9N9456", "");
    
    const response = await axios.put(baseUrl + "/whitelabel/userSettings/notifications", settings, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    return { status: response.status, message: response.data.message };
  } catch (error: any) {
    return {
      status: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to update notification settings'
    };
  }
};

export const updatePrivacySettings = async (token: string, settings: {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  version: string;
  method: 'banner' | 'settings';
  ipAddress?: string;
  userAgent?: string;
}) => {
  try {
    // Strip the B9I9N9456 suffix from the token
    const jwt = (token as string).replace("B9I9N9456", "");
    
    console.log(" Making API call to:", `${baseUrl}/whitelabel/userSettings/privacy`);
    console.log("📤 Request payload:", settings);
    console.log("🔑 Token (first 20 chars):", jwt.substring(0, 20) + "...");
    
    const response = await fetch(`${baseUrl}/whitelabel/userSettings/privacy`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify(settings),
    });

    console.log("📥 Response status:", response.status);
    console.log("📥 Response headers:", Object.fromEntries(response.headers.entries()));

    const data = await response.json();
    console.log("📥 Response data:", data);
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update privacy settings');
    }

    return { status: response.status, data };
  } catch (error) {
    console.error('❌ Error updating privacy settings:', error);
    throw error;
  }
};

// New function to get consent history
export const getConsentHistory = async (token: string) => {
  try {
    const response = await fetch(`${baseUrl}/whitelabel/userSettings/consentHistory`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token,
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to get consent history');
    }

    return data.results;
  } catch (error) {
    console.error('Error getting consent history:', error);
    throw error;
  }
};

// New Account Deletion Functions
export const requestAccountDeletion = async (
  token: string, 
  data: RequestAccountDeletionProps
): Promise<DeletionRequestResponse> => {
  try {
    if (!token) return { status: 401, message: 'No authentication token provided' };
    
    const jwt = (token as string).replace("B9I9N9456", "");
    
    const response = await axios.post(baseUrl + "/whitelabel/requestAccountDeletion", data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
    });

    return { 
      status: response.status, 
      message: response.data.message,
      deletionScheduledFor: response.data.deletionScheduledFor,
      cancellationToken: response.data.cancellationToken
    };
  } catch (error: any) {
    return {
      status: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to request account deletion'
    };
  }
};

export const cancelAccountDeletion = async (
  token: string, 
  cancellationToken: string
): Promise<{ message: string; status: number }> => {
  try {
    if (!token) return { status: 401, message: 'No authentication token provided' };
    
    const jwt = (token as string).replace("B9I9N9456", "");
    
    const response = await axios.post(baseUrl + "/whitelabel/cancelAccountDeletion", 
      { cancellationToken }, 
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    return { status: response.status, message: response.data.message };
  } catch (error: any) {
    return {
      status: error.response?.status || 500,
      message: error.response?.data?.message || 'Failed to cancel account deletion'
    };
  }
};

// Add this function to your existing user.ts file
export const changePassword = async (
  token: string,
  data: ChangePasswordProps
): Promise<ChangePasswordResponse> => {
  try {
    console.log('🔐 Starting changePassword...');
    console.log('🔑 Token received:', !!token);
    
    if (!token) {
      console.log('❌ No JWT token provided');
      return { status: 401, message: 'No authentication token provided' };
    }

    // Use the same token handling as the working functionality
    const jwt = (token as string).replace("B9I9N9456", "");
    
    console.log('🔑 JWT token length:', jwt.length);
    console.log('🔑 JWT token preview:', jwt.substring(0, 50) + '...');

    // Use the correct endpoint that matches your backend
    console.log('🌐 Making API call to:', baseUrl + "/whitelabel/changePassword");
    
    const response = await axios.put(baseUrl + "/whitelabel/changePassword", 
      { 
        password: data.password,
        passwordVerify: data.passwordVerify 
      }, 
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    console.log('📡 API Response status:', response.status);
    console.log('📡 API Response data:', response.data);

    if (response.status === 200 && response.data) {
      console.log('✅ Password changed successfully');
      return {
        status: 200,
        message: response.data.message || 'Password changed successfully'
      };
    } else {
      console.log('❌ Failed to change password:', response.data);
      return {
        status: response.status || 400,
        message: response.data?.message || 'Failed to change password'
      };
    }
  } catch (error: any) {
    console.error('❌ Error changing password:', error);
    if (error.response) {
      console.error('❌ Error response:', error.response.data);
      console.error('❌ Error status:', error.response.status);
      return {
        status: error.response.status,
        message: error.response.data?.message || 'Failed to change password'
      };
    }
    return {
      status: 500,
      message: 'Network error occurred while changing password'
    };
  }
};